import { useRef, useCallback, useState } from "react";
import { Loader, PostCard } from "@/components/shared";
import { useGetRecentPosts, useCreatePost } from "@/lib/react-query/queries";
import SEOComponent from "@/components/shared/SEOComponent";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { buildSignInRedirect } from "@/lib/auth/redirect";

interface OutletContextType {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
}

const DEAL_CATEGORIES = [
  { key: "all", label: "All Deals" },
  { key: "physical-goods", label: "Physical Goods" },
  { key: "services", label: "Services" },
  { key: "digital", label: "Digital Products" },
  { key: "Others", label: "Others" }
];

const MAX_CHARS = 280;

const Home = () => {
  const { selectedCategory, setSelectedCategory } = useOutletContext<OutletContextType>();
  const { isAuthenticated, user } = useUserContext();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [quickPostText, setQuickPostText] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetRecentPosts(selectedCategory);

  const { mutateAsync: createPost, isLoading: isCreatingQuickPost } = useCreatePost();

  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastPostRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleQuickPost = async () => {
    if (!quickPostText.trim()) {
      toast({
        title: "Please write something",
        variant: "destructive"
      });
      return;
    }

    if (!isAuthenticated) {
      navigate(buildSignInRedirect('/home'));
      return;
    }

    try {
      await createPost({
        caption: quickPostText.trim(),
        userId: user.id,
        individual: "marketize",
        dealType: "Others",
        isSellingPost: false,
        isCommercialPost: false,
        tags: "",
        location: "",
        file: []
      });

      setQuickPostText("");
      setIsFocused(false);
      toast({
        title: "✨ Posted successfully!",
      });
    } catch (error) {
      console.error("Quick post error:", error);
      toast({
        title: "Failed to post",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const charsLeft = MAX_CHARS - quickPostText.length;
  const isOverLimit = charsLeft < 0;
  const isNearLimit = charsLeft <= 20 && charsLeft >= 0;

  const getProgressColor = () => {
    if (isOverLimit) return 'text-rose-500';
    if (isNearLimit) return 'text-yellow-500';
    return 'text-primary-500';
  };

  const getProgressPercent = () => {
    return Math.min((quickPostText.length / MAX_CHARS) * 100, 100);
  };

  if (isError) {
    return (
      <div className="flex flex-1">
        <div className="home-container">
          <p className="body-medium text-light-1">Something went wrong</p>
        </div>
      </div>
    );
  }

  const allPosts = data?.pages?.flatMap((page) => page.documents) ?? [];
  const filteredPosts = allPosts.filter((post) => {
    if (!post.isActivated) return false;
    return selectedCategory === "all" || post.category === selectedCategory;
  });

  const showNoResults = !isLoading && filteredPosts.length === 0;
  const showEndMessage = !hasNextPage && !isLoading && filteredPosts.length > 0;

  return (
    <>
<SEOComponent
  title="Products & Services | Busiconn"
  description="Discover products and services on Busiconn. Connect with sellers and freelancers, discuss your requirements, and make protected deals."
  canonicalUrl="https://busiconn.com/home"
  keywords="escrow platform India, online deal escrow, business deal protection, WhatsApp business escrow, trust infrastructure, secure online transactions, freelance escrow India, B2B deal safety"
  ogType="website"
  structuredData={{
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Busiconn",
    "description": "Trust and escrow infrastructure for online business deals. Busiconn enables secure negotiation, escrow-protected payments, and dispute resolution for deals happening over WhatsApp and social platforms.",
    "url": "https://busiconn.com/",
    "applicationCategory": "FinancialApplication",
    "operatingSystem": "Web",
    "provider": {
      "@type": "Organization",
      "name": "Busiconn",
      "url": "https://busiconn.com"
    }
  }}
/>

      {/* FOMO Bar */}
      <div className="bg-primary-600 text-white text-sm py-2 px-4 text-center font-medium">
        Unique Products✨| Money Protection🛡️|  Quick Payout💸
      </div>

      <div className="flex flex-1">
        <div className="home-container">
          {/* Simple Welcome Banner for Non-authenticated Users */}
          {!isAuthenticated && (
            <div className="bg-dark-2 rounded-xl p-6 mb-6 border border-dark-4">
              <h2 className="text-2xl font-semibold mb-2">
                Welcome to Busiconn
              </h2>
              <p className="text-light-3 mb-4">
                India’s trusted platform for safe, protected deals - chat, negotiate, and trade securely with anyone.
              </p>
              <div className="flex gap-3">
                <Button asChild>
                  <Link to="/sign-up">Get Started</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link className="text-gray-700" to={buildSignInRedirect('/home')}>Sign In</Link>
                </Button>
              </div>
            </div>
          )}

          {/* Deal Type Filter */}
          <div className="flex gap-3 my-4 flex-wrap">
            {DEAL_CATEGORIES.map(({ key, label }) => (
              <button
                key={key}
                className={`px-4 py-2 rounded-lg text-sm md:text-base transition-colors ${
                  selectedCategory === key
                    ? "bg-primary-500 text-white"
                    : "bg-light-2 text-dark-4 hover:bg-light-3"
                }`}
                onClick={() => setSelectedCategory(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Quick Post Section */}
          {isAuthenticated && (
            <div className="flex justify-center w-full mb-6">
              <div className={`w-full max-w-[600px] bg-dark-2 rounded-xl border transition-all duration-200 ${
                isFocused ? 'border-primary-500 shadow-lg shadow-primary-500/20' : 'border-dark-4'
              }`}>
                <div className="p-4">
                  <div className="flex gap-3 items-start">
                    <img 
                      src={user.imageUrl || "/assets/icons/profile-placeholder.svg"}
                      alt="profile"
                      className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <textarea
                        value={quickPostText}
                        onChange={(e) => setQuickPostText(e.target.value)}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => !quickPostText && setIsFocused(false)}
                        placeholder="Share your business updates... 💼✨"
                        className="w-full bg-transparent text-light-1 resize-none focus:outline-none placeholder:text-light-4 text-base"
                        rows={isFocused ? 3 : 1}
                        maxLength={MAX_CHARS + 50}
                        disabled={isCreatingQuickPost}
                      />
                    </div>
                  </div>

                  {(isFocused || quickPostText) && (
                    <div className="border-t border-dark-4 my-3"></div>
                  )}

                  {(isFocused || quickPostText) && (
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        {quickPostText.length > 0 && (
                          <div className="relative w-8 h-8 flex-shrink-0">
                            <svg className="w-full h-full transform -rotate-90">
                              <circle
                                cx="16"
                                cy="16"
                                r="12"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                fill="none"
                                className="text-dark-4"
                              />
                              <circle
                                cx="16"
                                cy="16"
                                r="12"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                fill="none"
                                className={`transition-all duration-300 ${getProgressColor()}`}
                                strokeDasharray={`${2 * Math.PI * 12}`}
                                strokeDashoffset={`${2 * Math.PI * 12 * (1 - getProgressPercent() / 100)}`}
                                strokeLinecap="round"
                              />
                            </svg>
                            {(isNearLimit || isOverLimit) && (
                              <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold ${getProgressColor()}`}>
                                {charsLeft}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {quickPostText && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setQuickPostText("");
                              setIsFocused(false);
                            }}
                            disabled={isCreatingQuickPost}
                            className="text-light-3 hover:text-light-1 hover:bg-dark-4 h-9"
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          onClick={handleQuickPost}
                          disabled={isCreatingQuickPost || !quickPostText.trim() || isOverLimit}
                          size="sm"
                          className={`rounded-full font-semibold transition-all min-w-[70px] h-9 ${
                            !quickPostText.trim() || isOverLimit
                              ? 'bg-primary-500/50 cursor-not-allowed'
                              : 'bg-primary-500 hover:bg-primary-600 hover:shadow-lg hover:shadow-primary-500/30'
                          }`}
                        >
                          {isCreatingQuickPost ? (
                            <>
                              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                              Posting
                            </>
                          ) : (
                            'Post'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {isOverLimit && (
                    <p className="text-xs text-rose-500 mt-2 flex items-center gap-1">
                      <span className="font-semibold">⚠️</span>
                      You've exceeded the character limit by {Math.abs(charsLeft)} characters
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isLoading && !data ? (
            <Loader />
          ) : (
            <>
              <ul className="flex flex-col flex-1 gap-6 w-full">
                {filteredPosts.map((post, index) => (
                  <li
                    key={post.$id}
                    ref={index === filteredPosts.length - 1 ? lastPostRef : null}
                    className="flex justify-center w-full"
                  >
                    <PostCard post={post} showDealBadge={true} />
                  </li>
                ))}
              </ul>

              {isFetchingNextPage && <Loader />}

              {showNoResults && (
                <div className="flex-center flex-col gap-4 mt-10">
                  <img src="/assets/icons/no-deals.svg" alt="No deals" className="h-24 w-24" />
                  <p className="text-light-3 text-center">
                    {selectedCategory === "all" 
                      ? "No deals available yet" 
                      : `No ${selectedCategory.replace("-", " ")} deals found`}
                  </p>
                  {!isAuthenticated && (
                    <div className="mt-4">
                      <Button asChild>
                        <Link to="/sign-up">Create an Account to Post</Link>
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {showEndMessage && (
                <div className="text-center mt-8 mb-8">
                  <p className="text-light-3">You've reached the end</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;
